// src/config/env.ts
// Single typed gateway to import.meta.env. NO secrets are logged.
// VITE_API_BASE_URL is empty in local dev (Vite proxy forwards /api + /connect).
export const env = {
  apiBaseUrl: (import.meta.env.VITE_API_BASE_URL ?? '') as string,
  // Scrips.Workflows service base (PROD-738 Clinical Timeline). The
  // appointment care-pathway controllers (`workflows/appointments/...`) are
  // hosted by Scrips.Workflows on a SEPARATE host from the PM-API — in
  // deployed builds `VITE_API_BASE_URL` (dev-api.scrips.com) has no matching
  // route, so those calls MUST target this host instead. Default EMPTY so
  // local dev falls through to the Vite `/workflows` proxy (see vite.config),
  // and a mis-configured non-dev environment fails safe (no workflow calls)
  // rather than silently hitting the wrong host. Set per-environment
  // (dev → https://dev-workflows.scrips.com, see .env.example).
  workflowsBaseUrl: (import.meta.env.VITE_WORKFLOWS_BASE_URL ?? '') as string,
  appVersion: (import.meta.env.VITE_APP_VERSION ?? '0.0.0') as string,
  // Telemedicine iframe app base (PROD-1276). The encounter embeds
  // `${videoBaseUrl}/?AppointmentId=&ProviderId=` when the visit is Virtual.
  // Default EMPTY so a missing config fails safe (no telemedicine) rather than
  // silently routing a non-dev environment at dev infra — set VITE_VIDEO_BASE_URL
  // per environment (dev → https://dev-telemedicine.scrips.com, see .env.example).
  videoBaseUrl: (import.meta.env.VITE_VIDEO_BASE_URL ?? '') as string,
  // Ambient scribe WebSocket host (PROD-1944). NOT the same service as videoBaseUrl above:
  // that is the telemedicine UI (dev-telemedicine), this is the video API that terminates
  // the ambient stream (dev-videoapp). The panel appends `/AmbientScribe/Stream/{mode}`.
  //
  // Default EMPTY, and the panel is not rendered without it — same fail-safe reasoning as
  // videoBaseUrl. An ambient session streams consultation audio, so a misconfigured
  // environment pointing at dev infra would send one clinic's PHI to another's server.
  ambientBaseUrl: (import.meta.env.VITE_AMBIENT_BASE_URL ?? '') as string,
  // Malaffi HIE SSO initiator (PROD-1830). The encounter's Malaffi tab opens
  // `${malaffiSsoUrl}?q1={patient MRN}&q2={user id}` in a popup — Flutter
  // `getMalaffiSSOBase()` (api_constants.dart:154-167) switches this per ENV:
  //   DEV/QA/UAT → https://malaffi-sso-uat.scrips.com/SSOInitiator.aspx
  //   PROD       → https://malaffi-sso-prod.scrips.com/SSOInitiator.aspx
  // Default EMPTY so a mis-configured environment fails safe (the tab is hidden — never a
  // silent hand-off of a patient MRN to the WRONG Malaffi tenant, which a baked-in UAT
  // fallback would do in production). Set per-environment (see .env.example).
  malaffiSsoUrl: (import.meta.env.VITE_MALAFFI_SSO_URL ?? '') as string,
  // Lab-linkage rollout flag (PROD-1126/1144). Default OFF — the actionable
  // lab-order fulfillment surface (advance status / revert / result-entry)
  // only mounts when this is explicitly "true". Combined with the
  // `Manage_Orders_Prescriptions` permission at the mount site so the
  // write surface is both flag- and permission-gated.
  labLinkageEnabled: (import.meta.env.VITE_LAB_LINKAGE_ENABLED ?? '') === 'true',
  // Clinical display-name dictionary rollout flag (PROD-1656 Phase A). Default OFF —
  // `useClinicalDisplayNames` issues zero network calls and resolves an empty map until
  // this is explicitly "true", so the FE seam merges safely before the backend batch
  // endpoint exists. Flip per-environment once Andrew's dictionary endpoint lands.
  displayNamesEnabled: (import.meta.env.VITE_DISPLAY_NAMES ?? '') === 'true',
  // Care-model advance on sign (care-model slice, contract C3). Default OFF — a successful sign
  // issues no Scrips.Workflows call until this is explicitly "true". Without the flag, a bundle
  // shipped ahead of the Workflows endpoint would warn on EVERY sign, for every patient, in every
  // organisation, whether or not it uses care models. Turn it on per environment once
  // POST /workflows/care-models/enrolments/advance is deployed there.
  careModelAdvanceEnabled: (import.meta.env.VITE_CARE_MODEL_ADVANCE ?? '') === 'true',
  // Order sets, apply from the A/P rail (PROD-2712). Default OFF: the rail keeps its ICD-10 |
  // Bundles toggle, byte for byte, until this is explicitly "true". It stays off in prod until
  // PROD-2673's AC1 (safety checks on the apply-to-sign path) and AC2 (clinical-equivalence
  // duplicate rules) are settled. Set in dev-web.yml and .env.development only.
  orderSetsApplyEnabled: (import.meta.env.VITE_ORDER_SETS_APPLY ?? '') === 'true',
  // Cohort enrolment from the patient list (PROD-3206): enrol one patient (row menu) or many
  // (row selection) into a Scrips.Patient cohort. Default OFF in every environment, dev included:
  // the C1 cohort endpoints are not deployed yet. With the flag off, no cohort UI renders and no
  // cohort call is made. Turn it on per environment once `api/Cohorts` is live there.
  cohortEnrolmentEnabled: (import.meta.env.VITE_COHORT_ENROLMENT ?? '') === 'true',
  // The Analytics page (?x=analytics): the clinician prototype's seeded clinic numbers, fixed at 15 Oct 2026. Default
  // OFF in every environment; set to "true" in dev-web.yml and .env.development ONLY, so no production browser can
  // turn it on. A browser still needs ?x=analytics as well (off by default).
  analyticsEnabled: (import.meta.env.VITE_ANALYTICS ?? '') === 'true',
  // "Assign care plan" in the A&P (PROD-3206) reads ON by default in this deploy, without `?x=assign-care-plan`
  // (ELLMS, 23 Oct 2026). Default OFF in every environment. Set to "true" in dev-web.yml ONLY. A browser can still turn
  // it off with `?x=-assign-care-plan`. Revert = delete the dev-web.yml line; the switch then reads OFF again.
  assignCarePlanDefaultOn: (import.meta.env.VITE_ASSIGN_CARE_PLAN_DEFAULT_ON ?? '') === 'true',
  // The A&P referral to a speciality (`referrals`, PROD-1395 / PROD-3336) and the backslash insert (`slashInsert`, #769) read ON by default in
  // this deploy, without `?x=referrals` / `?x=slash-insert` (Andrew, 24 Oct 2026). Default OFF in every environment. Set to
  // "true" in dev-web.yml ONLY. A browser can still turn either off with `?x=-referrals` / `?x=-slash-insert`.
  // Revert = delete the dev-web.yml line; the switch then reads OFF again.
  referralsDefaultOn: (import.meta.env.VITE_REFERRALS_DEFAULT_ON ?? '') === 'true',
  slashInsertDefaultOn: (import.meta.env.VITE_SLASH_INSERT_DEFAULT_ON ?? '') === 'true',
  // The required-item sign gate (PROD-2743, C10.2). Default OFF in every environment, dev included:
  // the dev templates mark many items required, so the gate would block nearly every sign at once.
  // Turn it on per environment once the templates' required items are reviewed.
  requiredItemsSignGateEnabled: (import.meta.env.VITE_REQUIRED_ITEMS_SIGN_GATE ?? '') === 'true',
  // Ambient diarization trace (PROD-1996 follow-up). Default OFF, and deliberately NOT gated on
  // `import.meta.env.DEV`: QA tests against the DEPLOYED dev site, where DEV is false — the same
  // trap `dev-auto-login.ts` documents. Set to "true" in dev-web.yml ONLY, so the control is
  // absent from the qa/uat/prod bundles rather than merely hidden in them.
  //
  // It gates a clipboard export of raw recogniser speaker ids. Those never render — see
  // `diarization-trace.ts` for why an export rather than a view — so this flag controls whether
  // the copy control exists, not whether identifiers appear on screen. They never do.
  diarizationTraceEnabled: (import.meta.env.VITE_AMBIENT_DIARIZATION_TRACE ?? '') === 'true',
  // Scrips Intelligence agent engine (scrips-clinician-demo scripsServer.ts), served on dev at the SAME origin under
  // /api/intelligence (an Ingress path on dev-practitioner-v2). Default OFF, and deliberately NOT gated on
  // `import.meta.env.DEV` (the deployed dev site has DEV false; see diarizationTraceEnabled). Set to "true" in
  // dev-web.yml ONLY, so the panel is absent from the other bundles. The engine itself serves only allow-listed
  // patients and writes nothing to the record.
  intelligenceEngineEnabled: (import.meta.env.VITE_INTELLIGENCE_ENGINE ?? '') === 'true',
  // L360 A3 "Raise demo hold" (a real hold on A3 in the dev run, for demos). Default OFF, and deliberately NOT gated
  // on `import.meta.env.DEV`: the DEPLOYED dev site has DEV false (see diarizationTraceEnabled). Set to "true" in
  // dev-web.yml ONLY, so the button is absent from the other bundles rather than hidden in them.
  // Also gates the A3 "Release hold" control (same dev-only reason).
  l360DemoHoldEnabled: (import.meta.env.VITE_L360_DEMO_HOLD ?? '') === 'true',
  // L360 connected-flow events to Workflows (`postL360Event`). Default OFF. Dev only: set to "true" in dev-web.yml ONLY,
  // so the call is absent from the other bundles.
  l360EventsEnabled: (import.meta.env.VITE_L360_EVENTS ?? '') === 'true',
  // Document Intake screens (PROD-3204): the review screen route and, later, the patient inbox. Default OFF. Turn it
  // on per environment once the Scrips.Patient Intake routes are deployed there with `Intake:Enabled=true`; until then
  // every intake route answers 404 `intake_disabled`.
  documentIntakeEnabled: (import.meta.env.VITE_DOCUMENT_INTAKE ?? '') === 'true',
  // Pre-visit questionnaires the summary's "Patient-reported" section reads (comma list of questionnaire ids: QuestionBank
  // GUIDs, or the vendored NLM PHQ-9 id `44249-1-x` the pre-visit Studio step sends, matched on the response itself). Empty by default = the section reads nothing. Shown only with
  // `?x=patient-reported` too.
  previsitQuestionnaireIds: (import.meta.env.VITE_PREVISIT_QUESTIONNAIRE_IDS ?? '') as string,
  // Dev-only password-grant bringup shim (creds from .env.local). When unset,
  // the app shows the login page instead of auto-bootstrapping a session.
  auth: {
    // OAuth client id for the password grant. This app must NOT share a client id
    // with the legacy Flutter Provider app: Scrips.Identity gates per-organization
    // app access on client_id (dbo.OrganizationAppAccess + AppSurfaceMap), so while
    // both send "Scrips.Provider" a flip blocks BOTH apps instead of migrating
    // anyone. Local dev falls back to Scrips.Practitioner.V2 so a fresh checkout
    // works with no .env.local setup. Deployed builds have
    // `import.meta.env.DEV === false`, so they still require the CI-injected
    // VITE_AUTH_CLIENT_ID (no dev fallback ships).
    //
    // clientSecret has no fallback on purpose. It previously defaulted to the
    // literal 'secret' inside token-service.ts, which shipped a live credential in
    // the public JS bundle; login now fails loudly with `invalid_client` when the
    // secret is not configured rather than silently using a shared one.
    clientId: ((import.meta.env.VITE_AUTH_CLIENT_ID ??
      (import.meta.env.DEV ? 'Scrips.Practitioner.V2' : '')) || '') as string,
    clientSecret: (import.meta.env.VITE_AUTH_CLIENT_SECRET ?? '') as string,
    username: (import.meta.env.VITE_AUTH_USERNAME ?? '') as string,
    password: (import.meta.env.VITE_AUTH_PASSWORD ?? '') as string,
    scope: (import.meta.env.VITE_AUTH_SCOPE ?? 'openid offline_access') as string,
  },
} as const;

export const hasBringupCreds = (): boolean =>
  Boolean(env.auth.clientId && env.auth.username && env.auth.password);
